import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '@/lib/api';
import { Link } from 'react-router-dom';
import { House, MagnifyingGlass, ShieldStar, Users, CheckCircle, ClockCounterClockwise, ArrowRight, Eye } from '@phosphor-icons/react';
import { Input } from '@/components/ui/input';

export default function AdminHouseholds() {
    const [q, setQ] = useState('');
    const { data: households = [], isLoading } = useQuery({
        queryKey: ['admin-households'],
        queryFn: async () => (await api.get('/admin/households')).data,
    });

    const filtered = useMemo(() => {
        const s = q.trim().toLowerCase();
        if (!s) return households;
        return households.filter(h =>
            (h.name || '').toLowerCase().includes(s) ||
            h.coordinators?.some(c => (c.name || '').toLowerCase().includes(s) || (c.email || '').toLowerCase().includes(s))
        );
    }, [households, q]);

    return (
        <div className="px-6 md:px-10 py-8 max-w-7xl" data-testid="admin-households-root">
            <div className="flex items-end justify-between mb-6">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500 flex items-center gap-1.5">
                        <ShieldStar size={13} weight="duotone" /> Tanow staff
                    </div>
                    <h1 className="font-heading text-4xl md:text-5xl text-slate-900 mt-1">All households</h1>
                    <p className="text-slate-600 mt-2 max-w-2xl">Every household on Tanow. View read-only, or join a household as a coordinator to help troubleshoot.</p>
                </div>
                <Link to="/admin/access-log" className="text-sm text-primary hover:underline inline-flex items-center gap-1" data-testid="admin-households-access-log-link">
                    <ClockCounterClockwise size={14} /> Admin access log
                </Link>
            </div>

            <div className="mb-4 relative max-w-md">
                <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                    data-testid="admin-households-search"
                    placeholder="Search by household or coordinator…"
                    value={q}
                    onChange={e => setQ(e.target.value)}
                    className="pl-9"
                />
            </div>

            <div className="rounded-xl border border-slate-200 bg-card shadow-sm overflow-hidden">
                <div className="grid grid-cols-[1.4fr_1.4fr_80px_120px_120px_60px] px-5 py-3 border-b border-slate-200 text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
                    <div>Household</div>
                    <div>Coordinators</div>
                    <div>People</div>
                    <div>Needs attention</div>
                    <div>Open items</div>
                    <div />
                </div>
                {isLoading ? (
                    <div className="px-5 py-8 text-sm text-slate-500 text-center">Loading…</div>
                ) : filtered.length === 0 ? (
                    <div className="px-5 py-8 text-sm text-slate-500 text-center">No households found.</div>
                ) : (
                    <div className="divide-y divide-slate-100">
                        {filtered.map(h => (
                            <div key={h.facility_id} data-testid="admin-household-row" className="grid grid-cols-[1.4fr_1.4fr_80px_120px_120px_60px] px-5 py-3 items-center text-sm">
                                <div className="min-w-0">
                                    <div className="font-medium text-slate-900 truncate flex items-center gap-2">
                                        <House size={14} className="text-slate-400" />
                                        <span className="truncate">{h.name}</span>
                                        {h.is_current_user_coordinator && (
                                            <span className="text-[9px] uppercase tracking-widest bg-emerald-50 text-emerald-800 px-1.5 py-0.5 rounded flex items-center gap-1">
                                                <CheckCircle size={10} weight="fill" /> Joined
                                            </span>
                                        )}
                                    </div>
                                    <div className="text-[11px] text-slate-500 truncate">{h.kind || '—'}</div>
                                </div>
                                <div className="text-slate-700 truncate">
                                    {h.coordinators?.length ? (
                                        <div className="space-y-0.5">
                                            {h.coordinators.slice(0, 2).map(c => (
                                                <div key={c.user_id} className="truncate">
                                                    <span className="text-slate-900">{c.name}</span>
                                                    <span className="text-slate-500 text-[11px] ml-1">{c.email}</span>
                                                </div>
                                            ))}
                                            {h.coordinators.length > 2 && (
                                                <div className="text-[11px] text-slate-500">+{h.coordinators.length - 2} more</div>
                                            )}
                                        </div>
                                    ) : <span className="text-slate-400">No coordinators</span>}
                                </div>
                                <div className="text-slate-800 flex items-center gap-1"><Users size={12} className="text-slate-400" />{h.member_count}</div>
                                <div>
                                    {h.open_items?.attention > 0 ? (
                                        <span className="text-[10px] uppercase tracking-widest bg-red-50 text-red-800 px-2 py-0.5 rounded font-semibold">
                                            {h.open_items.attention} flagged
                                        </span>
                                    ) : (
                                        <span className="text-slate-400">—</span>
                                    )}
                                </div>
                                <div className="text-slate-700 text-[12px]">
                                    <div>{h.open_items?.waiting || 0} waiting</div>
                                    <div className="text-slate-500">{h.open_items?.todos || 0} to-do{h.open_items?.todos === 1 ? '' : 's'}</div>
                                </div>
                                <div className="text-right">
                                    <Link
                                        to={`/admin/households/${h.facility_id}`}
                                        data-testid="admin-household-view"
                                        className="inline-flex items-center gap-1 text-primary hover:underline text-sm"
                                    >
                                        <Eye size={14} /> <ArrowRight size={12} />
                                    </Link>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
